/**
 * Search-engine metadata for every Help Center page: one definition, read by
 * the live pages (react-helmet) and by the build-time pre-renderer, so the
 * HTML Google fetches and the page a visitor sees always agree.
 *
 * Structured data names Defera as author and publisher; the visible byline
 * names the SI leader who wrote it; its SI badge carries the disclosure.
 */
import { HELP_BASE_PATH, HELP_SITE_ORIGIN } from './index.js';
import { headTags, SHARE_IMAGE } from '../seo/site.js';

const SITE_NAME = 'Defera Help Center';
const LOGO = `${HELP_SITE_ORIGIN}/defera-transparent.png`;
export const OG_IMAGE = SHARE_IMAGE.url;
const ORG = { '@type': 'Organization', name: 'Defera', url: HELP_SITE_ORIGIN, logo: { '@type': 'ImageObject', url: LOGO } };

const crumb = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({ '@type': 'ListItem', position: i + 1, name: it.name, item: `${HELP_SITE_ORIGIN}${it.path}` })),
});

export function homeMeta(help) {
  const count = help.articles.length;
  return {
    title: 'Defera Help Center: answers to every question',
    description: `Guides and answers for booking, payments, changes, your confirmation number, Miles and more${count ? `: ${count} articles` : ''}, from the team at Defera.`,
    canonical: `${HELP_SITE_ORIGIN}${HELP_BASE_PATH}`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: SITE_NAME,
        url: `${HELP_SITE_ORIGIN}${HELP_BASE_PATH}`,
        publisher: ORG,
        potentialAction: {
          '@type': 'SearchAction',
          target: `${HELP_SITE_ORIGIN}${HELP_BASE_PATH}?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      },
    ],
  };
}

export function sectionMeta(section) {
  return {
    title: `${section.title} | ${SITE_NAME}`,
    description: `${section.description} ${section.articles.length} articles from the Defera Help Center.`.slice(0, 170),
    canonical: `${HELP_SITE_ORIGIN}${section.path}`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: section.title,
        description: section.description,
        url: `${HELP_SITE_ORIGIN}${section.path}`,
        hasPart: section.articles.map((a) => ({ '@type': 'Article', headline: a.title, url: `${HELP_SITE_ORIGIN}${a.path}` })),
      },
      crumb([{ name: 'Help Center', path: HELP_BASE_PATH }, { name: section.title, path: section.path }]),
    ],
  };
}

export function articleMeta(article) {
  return {
    title: `${article.title} | ${SITE_NAME}`,
    description: article.summary,
    canonical: `${HELP_SITE_ORIGIN}${article.path}`,
    type: 'article',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.summary,
        dateModified: article.updated,
        author: ORG,
        publisher: ORG,
        mainEntityOfPage: { '@type': 'WebPage', '@id': `${HELP_SITE_ORIGIN}${article.path}` },
        articleSection: article.sectionTitle,
        keywords: (article.keywords || []).join(', ') || undefined,
      },
      crumb([
        { name: 'Help Center', path: HELP_BASE_PATH },
        { name: article.sectionTitle, path: `${HELP_BASE_PATH}/${article.sectionId}` },
        { name: article.title, path: article.path },
      ]),
    ],
  };
}

/** The <head> tags for a page's metadata: the site-wide builder (src/content/seo/site.js). */
export { headTags };

/** Every Help Center URL, for the sitemap. */
export function helpUrls(help) {
  return [
    { path: HELP_BASE_PATH, lastmod: latest(help.articles) },
    ...help.sections.map((s) => ({ path: s.path, lastmod: latest(s.articles) })),
    ...help.articles.map((a) => ({ path: a.path, lastmod: a.updated })),
  ];
}

function latest(articles) {
  return (articles || []).map((a) => a.updated).sort().pop() || undefined;
}
